import { Table } from '@shinetools/lumen-react'
import type { ReactElement, ReactNode } from 'react'

import type { ExpertBalanceSheetColumnDef } from '../../../../hooks/useExpertBalanceSheetColumns'
import type { ExpertBalanceSheetTableRow } from '../../../../types/expertBalanceSheetView'
import { EmptyStateRow } from '../EmptyStateRow'

interface ExpertBalanceSheetTableBodyProps<Row extends ExpertBalanceSheetTableRow> {
  columns: ExpertBalanceSheetColumnDef<Row>[]
  rows: Row[]
  isEmpty: boolean
  isError: boolean
  emptyRowInfo: ReactNode
}

export const ExpertBalanceSheetTableBody = <Row extends ExpertBalanceSheetTableRow>({
  columns,
  rows,
  isEmpty,
  isError,
  emptyRowInfo,
}: ExpertBalanceSheetTableBodyProps<Row>): ReactElement => {
  const showEmptyState = isEmpty || isError || !rows.length
  const bodyRows = rows.filter((row) => row.kind !== 'total')
  const totalRows = rows.filter((row) => row.kind === 'total')

  const renderRow = (row: Row): ReactElement => (
    <Table.Row highlighted={row.kind === 'subtotal' || row.kind === 'total'} key={row.id}>
      {columns.map((column) => (
        <Table.Cell
          align={typeof column.align === 'function' ? column.align(row) : column.align}
          highlighted={column.highlighted}
          key={column.id}
        >
          {column.Cell(row)}
        </Table.Cell>
      ))}
    </Table.Row>
  )

  if (showEmptyState) {
    return (
      <Table.Body>
        <EmptyStateRow colSpan={columns.length} emptyRowInfo={emptyRowInfo} />
      </Table.Body>
    )
  }

  return (
    <>
      <Table.Body>{bodyRows.map(renderRow)}</Table.Body>
      {totalRows.length > 0 && <Table.Foot>{totalRows.map(renderRow)}</Table.Foot>}
    </>
  )
}
